import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import { Check, Sparkles } from 'lucide-react'
import { offersService, siteSettingsService } from '../services/database.js'

function Offers() {
  const { language, t, convertPrice, getCurrencySymbol } = useLanguage()
  const [offers, setOffers] = useState([])
  const [whatsappNumber, setWhatsappNumber] = useState('966500000000')

  useEffect(() => {
    const loadOffers = async () => {
      try {
        const offersData = await offersService.getAll()
        if (offersData && offersData.length > 0) {
          setOffers(offersData.filter(offer => offer.active))
        } else {
          // Fallback to default offers if no data in database
          setOffers([
            {
              name: language === 'ar' ? 'اشتراك شهري' : 'Monthly Subscription',
              price: '700000',
              duration: language === 'ar' ? 'شهر' : 'Month',
              features: language === 'ar' 
                ? ['دخول غير محدود', 'استخدام جميع الأجهزة', 'لوكر شخصي', 'دش وساونا']
                : ['Unlimited Access', 'All Equipment', 'Personal Locker', 'Shower & Sauna'],
              active: true
            },
            {
              name: language === 'ar' ? 'اشتراك 3 أشهر' : '3 Months Subscription',
              price: '1750000',
              duration: language === 'ar' ? '3 أشهر' : '3 Months',
              features: language === 'ar' 
                ? ['كل ما في الشهري', 'تدريب شخصي (جلسة واحدة)', 'خطة غذائية', 'تقييم شهري']
                : ['Everything in Monthly', 'Personal Training (1 session)', 'Nutrition Plan', 'Monthly Evaluation'],
              active: true
            },
            {
              name: language === 'ar' ? 'اشتراك سنوي' : 'Annual Subscription',
              price: '5250000',
              duration: language === 'ar' ? 'سنة' : 'Year',
              features: language === 'ar' 
                ? ['كل ما في 3 أشهر', 'تدريب شخصي (جلسة واحدة شهرياً)', 'خطة غذائية مخصصة', 'وصول VIP']
                : ['Everything in 3 Months', 'Personal Training (1 monthly session)', 'Custom Nutrition Plan', 'VIP Access'],
              popular: true,
              active: true
            }
          ])
        }
      } catch (error) {
        console.error('Error loading offers:', error)
        // Fallback to localStorage if database fails
        const savedOffers = localStorage.getItem('oxygen_offers')
        if (savedOffers) {
          const parsed = JSON.parse(savedOffers)
          setOffers(parsed.filter(offer => offer.active))
        }
      }
    }
    
    // Load offers immediately
    loadOffers()
    
    // Set up periodic sync to refresh data
    const syncInterval = setInterval(() => {
      if (navigator.onLine) {
        loadOffers()
      }
    }, 30000) // Sync every 30 seconds
    
    // Listen for visibility changes to sync when app becomes active
    const handleVisibilityChange = () => {
      if (!document.hidden && navigator.onLine) {
        loadOffers()
      }
    }
    
    // Listen for online events
    const handleOnline = () => {
      loadOffers()
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('online', handleOnline)
    
    return () => {
      clearInterval(syncInterval)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('online', handleOnline)
    }
  }, [language])

  // Load WhatsApp number from site settings
  useEffect(() => {
    const loadWhatsappNumber = async () => {
      try {
        const settingsData = await siteSettingsService.getAll()
        if (settingsData && settingsData.length > 0) {
          setWhatsappNumber(settingsData[0].whatsapp_number || '966500000000')
        }
      } catch (error) {
        console.error('Error loading WhatsApp number:', error)
      }
    }
    loadWhatsappNumber()
  }, [])

  useEffect(() => {
    const handleStorageChange = () => {
      const savedOffers = localStorage.getItem('oxygen_offers')
      if (savedOffers) {
        const parsed = JSON.parse(savedOffers)
        setOffers(parsed.filter(offer => offer.active))
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const handleSubscribe = (offerName) => {
    const message = language === 'ar'
      ? `السلام عليكم، أريد الاشتراك في عرض: ${offerName}`
      : `Hello, I want to subscribe to: ${offerName}`
    const cleanNumber = whatsappNumber.replace(/[^0-9]/g, '')
    const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`
    window.open(whatsappUrl, '_blank')
  }

  return (
    <section id="offers" className="py-24 bg-dark relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(255,215,0,0.05),transparent_50%)]" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block px-4 py-2 rounded-full bg-yellow-400/10 text-yellow-400 text-sm font-medium mb-4">
            {language === 'ar' ? 'عروضنا' : 'Our Offers'}
          </span>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
            {t('choosePlan')} <span className="text-gradient">{language === 'ar' ? 'خطتك' : 'Your Plan'}</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            {t('flexibleSubscriptions')}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {offers.map((offer, index) => (
            <motion.div
              key={index}
              className={`relative rounded-2xl p-8 ${
                offer.popular
                  ? 'bg-gradient-to-br from-yellow-400/20 to-orange-500/20 border-2 border-yellow-400/50 scale-105'
                  : 'bg-gray-900/50 border border-white/5'
              }`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              {offer.popular && (
                <div className="absolute -top-4 right-1/2 transform translate-x-1/2">
                  <span className="inline-flex items-center gap-1 px-4 py-1 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-sm font-bold">
                    <Sparkles className="w-4 h-4" />
                    {t('mostPopular')}
                  </span>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="text-xl font-bold text-white mb-4">{offer.name}</h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-5xl font-black text-gradient">
                    {offer.currency === 'all' ? offer.price : offer.price}
                  </span>
                  <span className="text-gray-400">
                    {offer.currency === 'all' 
                      ? t('all')
                      : (offer.currency?.toUpperCase() || 'SAR')
                    }
                  </span>
                </div>
                <span className="text-gray-400 text-sm">/ {offer.period || offer.duration}</span>
              </div>

              <ul className="space-y-4 mb-8">
                {Array.isArray(offer.features) && offer.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-yellow-400" />
                    </div>
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSubscribe(offer.name)}
                className={`w-full py-4 rounded-full font-bold transition-all duration-300 ${
                  offer.popular
                    ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-black hover:shadow-lg hover:shadow-yellow-400/25'
                    : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'
                }`}
              >
                {t('subscribeNow')}
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default Offers
